import React, { useState, useEffect } from 'react';
import { DataSource, CreateDataSourceRequest } from '../types';
import { useNotification } from '../contexts/NotificationContext';
import { useModal } from '../contexts/ModalContext';
import { ModalActions, ModalFormField } from '../components/ModalComponents';
import { networkManager } from '../services/NetworkManager';

// Create Data Source Form Component
interface CreateDataSourceFormProps {
  onSubmit: (data: CreateDataSourceRequest) => Promise<void>;
  onCancel: () => void;
  submitting: boolean;
  serviceAccountEmail?: string;
  hasCredentials?: boolean;
}

const CreateDataSourceForm: React.FC<CreateDataSourceFormProps> = ({ onSubmit, onCancel, submitting, serviceAccountEmail, hasCredentials = true }) => {
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState<CreateDataSourceRequest>({
    title: '',
    description: '',
    datasourceURL: ''
  });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => {
        setCopied(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  const handleCopyEmail = async () => {
    if (!serviceAccountEmail) return;
    
    try {
      await navigator.clipboard.writeText(serviceAccountEmail);
      setCopied(true);
      showNotification('Email copied to clipboard', 'success', 3000);
    } catch (err) {
      console.error('Failed to copy email:', err);
      showNotification('Failed to copy email', 'error', 3000);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.datasourceURL.trim()) {
      return;
    }
    try {
      await onSubmit(formData);
      // Only clear form if submission was successful
      setFormData({ title: '', description: '', datasourceURL: '' });
    } catch (error) {
      // Keep form data on error so user can correct it
      // Error is already handled in handleCreateDataSource
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <ModalFormField label="Title" required>
        <input
          type="text"
          required
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter data source title"
        />
      </ModalFormField>

      <ModalFormField label="Description">
        <textarea
          value={formData.description || ''}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter data source description (optional)"
          rows={3}
        />
      </ModalFormField>

      <ModalFormField label="Data Source URL" required>
        <input
          type="url"
          required
          value={formData.datasourceURL}
          onChange={(e) => setFormData({ ...formData, datasourceURL: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="https://docs.google.com/spreadsheets/d/..."
        />
      </ModalFormField>

      {hasCredentials ? (
        <p className="text-sm text-gray-600 mt-2">
          Please share your Google Sheet with email{' '}
          <span className="inline-flex items-center gap-1">
            <span className="font-bold text-gray-900">{serviceAccountEmail || '[SERVICE_EMAIL]'}</span>
            {serviceAccountEmail && (
              <button
                type="button"
                onClick={handleCopyEmail}
                className={`inline-flex items-center justify-center p-1 rounded transition-colors ${
                  copied 
                    ? 'text-green-600 hover:text-green-700 hover:bg-green-50' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
                title={copied ? "Copied!" : "Copy email to clipboard"}
              >
                {copied ? (
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
            )}
          </span>
          {' '}before pressing Create
        </p>
      ) : (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600 font-medium">
            You must load your credentials before start adding data sources
          </p>
        </div>
      )}

      <ModalActions
        actions={[
          {
            label: 'Cancel',
            onClick: onCancel,
            variant: 'secondary'
          },
          {
            label: submitting ? 'Creating...' : 'Create',
            type: 'submit',
            variant: 'primary',
            disabled: submitting || !formData.title.trim() || !formData.datasourceURL.trim() || !hasCredentials,
            loading: submitting
          }
        ]}
      />
    </form>
  );
};

const DataSourcesPage: React.FC = () => {
  const { showNotification } = useNotification();
  const { openModal, closeModal } = useModal();
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [serviceAccountEmail, setServiceAccountEmail] = useState<string | undefined>(undefined);
  const [hasCredentials, setHasCredentials] = useState<boolean>(true);

  useEffect(() => {
    loadDataSources();
    loadOrganizationInfo();
  }, []);

  const loadOrganizationInfo = async () => {
    try {
      const orgInfo = await networkManager.getOrganizationInfo();
      setServiceAccountEmail(orgInfo.gServiceAccountEmail);
      setHasCredentials(orgInfo.hasCredentials);
    } catch (err) {
      console.error('Error loading organization info:', err);
      // Don't show error notification for this, as it's not critical
      setHasCredentials(false);
    }
  };

  const loadDataSources = async () => {
    try {
      setLoading(true);
      const data = await networkManager.getDataSources();
      // Filter out any null or undefined values and ensure data is an array
      const validData = Array.isArray(data) 
        ? data.filter((item): item is DataSource => item != null && item._id != null)
        : [];
      setDataSources(validData);
    } catch (err) {
      console.error('Error loading data sources:', err);
      showNotification('Failed to load data sources', 'error', 8000);
      setDataSources([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getUTCDate();
    const month = date.getUTCMonth() + 1;
    const year = date.getUTCFullYear();
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    
    return `${day}/${month}/${year} ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };


  const handleCreateDataSource = async (formData: CreateDataSourceRequest) => {
    if (!formData.title.trim() || !formData.datasourceURL.trim()) {
      showNotification('Please fill in all required fields', 'error', 5000);
      throw new Error('Validation failed');
    }

    try {
      setSubmitting(true);
      await networkManager.createDataSource(formData);
      closeModal('create-data-source');
      showNotification('Data source created successfully', 'success', 5000);
      // Refresh the list to show the new datasource
      await loadDataSources();
    } catch (error: any) {
      console.error('Error creating data source:', error);
      // Extract message from error, handling both string and JSON string cases
      let errorMessage = 'Failed to create data source';
      if (error.message) {
        try {
          // Try to parse if it's a JSON string
          const parsed = JSON.parse(error.message);
          errorMessage = parsed.message || parsed.error || error.message;
        } catch {
          // If not JSON, use the message directly
          errorMessage = error.message;
        }
      }
      showNotification(errorMessage, 'error', 8000);
      // Re-throw error so form knows to keep the data
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  const openCreateModal = () => {
    const modalContent = (
      <CreateDataSourceForm
        onSubmit={handleCreateDataSource}
        onCancel={() => closeModal('create-data-source')}
        submitting={submitting}
        serviceAccountEmail={serviceAccountEmail}
        hasCredentials={hasCredentials}
      />
    );

    openModal({
      id: 'create-data-source',
      title: 'Add New Data Source',
      content: modalContent,
      size: 'md'
    });
  };

  const handleDelete = async (id: string, title: string) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) {
      return;
    }

    try {
      await networkManager.deleteDataSource(id);
      setDataSources(prev => prev.filter(ds => ds._id !== id));
      showNotification('Data source deleted successfully', 'success', 5000);
    } catch (error: any) {
      console.error('Error deleting data source:', error);
      // Extract message from error, handling both string and JSON string cases
      let errorMessage = 'Failed to delete data source';
      if (error.message) {
        try {
          // Try to parse if it's a JSON string
          const parsed = JSON.parse(error.message);
          errorMessage = parsed.message || parsed.error || error.message;
        } catch {
          // If not JSON, use the message directly
          errorMessage = error.message;
        }
      }
      showNotification(errorMessage, 'error', 8000);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Loading data sources...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Data Sources</h1>
            <p className="text-gray-600 mt-1">Manage your spreadsheet data sources</p>
          </div>
          <button 
            onClick={openCreateModal}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 border border-blue-600 hover:border-blue-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-105"
          >
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Data Source
          </button>
        </div>
      </div>


      {/* Table */}
      <div className="table-container">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th className="w-48">Title</th>
                <th className="w-64">Data Source URL</th>
                <th className="w-40">Author</th>
                <th className="w-40">Created At</th>
                <th className="w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!dataSources || dataSources.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <svg className="h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                      </svg>
                      <p className="text-gray-500 text-lg font-medium">No data sources found</p>
                      <p className="text-gray-400 text-sm">Get started by adding your first data source</p>
                    </div>
                  </td>
                </tr>
              ) : (
                dataSources
                  .filter((dataSource): dataSource is DataSource => 
                    dataSource != null && 
                    dataSource._id != null && 
                    dataSource.title != null
                  )
                  .map((dataSource) => (
                    <tr key={dataSource._id}>
                      <td>
                        <div className="flex items-center space-x-2">
                          <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                          </svg>
                          <div>
                            <p className="font-medium text-gray-900">{dataSource.title || 'Untitled'}</p>
                            {dataSource.description && (
                              <p className="text-sm text-gray-500">{dataSource.description}</p>
                            )}
                            <p className="text-sm text-gray-400">ID: {dataSource._id}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="max-w-xs">
                          <a 
                            href={dataSource.datasourceURL || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline text-sm truncate block"
                            title={dataSource.datasourceURL || ''}
                          >
                            {dataSource.datasourceURL && dataSource.datasourceURL.length > 50 
                              ? `${dataSource.datasourceURL.substring(0, 50)}...`
                              : dataSource.datasourceURL || 'N/A'
                            }
                          </a>
                        </div>
                      </td>
                      <td>
                        <p className="text-sm text-gray-600">
                          {dataSource.author 
                            ? (typeof dataSource.author === 'string' 
                                ? dataSource.author 
                                : dataSource.author.name || 'N/A')
                            : 'N/A'}
                        </p>
                      </td>
                      <td>
                        <p className="text-sm text-gray-600">
                          {dataSource.createdAt ? formatDate(dataSource.createdAt) : 'N/A'}
                        </p>
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleDelete(dataSource._id, dataSource.title || 'Unknown')}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-700 hover:text-red-800 hover:bg-red-50 border border-red-200 hover:border-red-300 rounded-md transition-all duration-200"
                            title="Delete Data Source"
                          >
                            <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DataSourcesPage;
